import {useMemo, useRef, useState} from "react";
import * as Sentry from "@sentry/react";
import {User} from "../../../../../api/accessControl/User.ts";
import {BackendApi} from "../../../../../api/api.ts";
import {fetchDocumentContentBlob} from "../../../../../api/fetchDocumentContent.ts";
import {QueryKey} from "../../../../../api/queryKeys.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import type {OpenedDocument} from "../../../../../components/specific/DrillDown/index.ts";
import {PerMonthView} from "../../../../../components/specific/trialBalance/index.ts";
import type {
    PerMonthStatement,
    PeriodicBalanceSheetReport,
    PeriodicProfitAndLossReport,
} from "../../../../../components/specific/trialBalance/index.ts";
import {useBusiness__myBusiness} from "../../../../../generated/blueprint.ts";
import type {CurrencyEnum} from "../../../../../generated/enums.ts";

export function ReportsPage({currentUser}: {currentUser: User}) {
    const api = useMemo(() => new BackendApi(currentUser.getToken()), [currentUser]);
    const {data: business} = useBusiness__myBusiness(api, {queryKey: QueryKey.Business});
    const [statement, setStatement] = useState<PerMonthStatement>("profit-and-loss");
    const [openedDocument, setOpenedDocument] = useState<OpenedDocument | null>(null);
    const documentRequestRef = useRef(0);

    async function fetchProfitAndLossPerPeriod(
        startDate: string,
        endDate: string,
        currency?: CurrencyEnum,
        showPredictedAccounts: boolean = true,
        signal?: AbortSignal,
    ) {
        const response = await api.withAbortSignal(signal).Entrepreneur__getProfitAndLossPerPeriod(
            startDate,
            endDate,
            currency,
            showPredictedAccounts,
        );
        return response as unknown as PeriodicProfitAndLossReport;
    }

    async function fetchBalanceSheetPerPeriod(
        startDate: string,
        endDate: string,
        currency?: CurrencyEnum,
        showPredictedAccounts: boolean = true,
        signal?: AbortSignal,
    ) {
        const response = await api.withAbortSignal(signal).Entrepreneur__getBalanceSheetPerPeriod(
            startDate,
            endDate,
            currency,
            showPredictedAccounts,
        );
        return response as unknown as PeriodicBalanceSheetReport;
    }

    async function openDocument(documentId: string) {
        const request = ++documentRequestRef.current;
        setOpenedDocument({isLoading: true, content: null});
        try {
            const blob = await fetchDocumentContentBlob(api, "entrepreneur/get-document-content", {
                document_id: documentId,
            });
            if (request !== documentRequestRef.current) return;
            setOpenedDocument({
                isLoading: false,
                content: {file_name: "document", file_blob: blob, mimetype: blob.type},
            });
        } catch (error) {
            if (request !== documentRequestRef.current) return;
            Sentry.captureException(error);
            setOpenedDocument({isLoading: false, content: {file_name: "document", mimetype: ""}});
        }
    }

    function closeDocument() {
        documentRequestRef.current++;
        setOpenedDocument(null);
    }

    return (
        <Shell currentUser={currentUser} isLoading={!business} title="Rapportage">
            {business && (
                <PerMonthView
                    currentUser={currentUser}
                    businessId={business.id}
                    legalEntityType={business.legal_entity_type}
                    statement={statement}
                    onStatementChange={setStatement}
                    canSeeBalanceSheet={true}
                    fetchProfitAndLossPerPeriod={fetchProfitAndLossPerPeriod}
                    fetchBalanceSheetPerPeriod={fetchBalanceSheetPerPeriod}
                    onOpenDocument={(documentId) => void openDocument(documentId)}
                    openedDocument={openedDocument}
                    onCloseDocument={closeDocument}
                />
            )}
        </Shell>
    );
}
